import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import { Card, MoneyInput } from '@pflegenavi/web-components';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { FC, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { BalanceSagesCard } from './BalanceSagesCard';
import type { IManualPaymentFormValues } from './types';

export interface ManualPaymentCardViewProps {
  disabled: boolean;
  activeBalance: number | undefined;
  isDoingManualPayin: boolean;
  isDoingManualPayinWithBankAccount: boolean;
  formik: FormikProps<IManualPaymentFormValues>;
  onChangeAmount: (value: number | undefined) => void;
  onOpenModal: () => void;
  pendingChargesAmount: number;
}

export const ManualPaymentCardView: FC<ManualPaymentCardViewProps> = ({
  disabled,
  activeBalance,
  isDoingManualPayin,
  isDoingManualPayinWithBankAccount,
  formik,
  onChangeAmount,
  onOpenModal,
  pendingChargesAmount,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  return (
    <Card
      sx={{
        p: 3,
        maxHeight: '95vh',
      }}
    >
      <Stack direction="column" alignItems="flex-start" sx={{ mb: 3 }} gap={4}>
        <Stack gap={1.5} sx={{ width: 1 }}>
          {/* Removed pending payment warning to allow multiple concurrent payins */}
          <Typography sx={{ color: disabled ? 'grey.500' : undefined }}>
            {t('fm-triggered-payments.pay-now')}:
          </Typography>
          <MoneyInput
            id="amount"
            name="amount"
            value={formik.values.amount}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || disabled}
            onChange={onChangeAmount}
            label={t('fm-triggered-payments.amount-screen.top-up-amount')}
            fullWidth
            helperText={
              getIn(formik.touched, 'amount') && getIn(formik.errors, 'amount')
            }
            error={Boolean(
              getIn(formik.touched, 'amount') && getIn(formik.errors, 'amount')
            )}
          />
          <BalanceSagesCard
            disabled={disabled}
            balance={activeBalance ? activeBalance : 0}
            topUpAmount={
              formik.values.amount?.toString().length > 0
                ? formik.values.amount
                : 0
            }
            pendingChargesAmount={pendingChargesAmount}
          />
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <LoadingButton
          disabled={!formik.isValid || disabled}
          variant="contained"
          loading={
            formik.isSubmitting ||
            isDoingManualPayin ||
            isDoingManualPayinWithBankAccount
          }
          onClick={onOpenModal}
        >
          {t('fm-triggered-payments.pay-now')} (
          {fCurrencyCents(formik.values.amount)})
        </LoadingButton>
      </Stack>
    </Card>
  );
};
